import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createClient, type SupabaseClient } from "@supabase/supabase-js"
import type { CandidateApplicationEvent, CandidateApplicationResponse } from "@/types/vacancy"
import type { Database } from "@/types/supabase"
import { useAuth0AccessToken } from "@/hooks/useAuth0AccessToken"

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL!
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY!
const EDGE_PATH = "/functions/v1/get-candidate-application"

const debounce = <T extends (...args: any[]) => void>(fn: T, ms: number) => {
  let timeout: number | undefined
  return (...args: Parameters<T>) => {
    if (timeout) {
      window.clearTimeout(timeout)
    }
    timeout = window.setTimeout(() => fn(...args), ms)
  }
}

export function useCandidateApplication(
  candidateId: string | null | undefined,
  vacancyId: string | null | undefined,
  companyId: string | null | undefined
) {
  const [data, setData] = useState<CandidateApplicationResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const sbRef = useRef<SupabaseClient | null>(null)
  if (!sbRef.current) {
    sbRef.current = createClient(SUPABASE_URL, SUPABASE_ANON)
  }
  const supabase = sbRef.current

  const getAccessToken = useAuth0AccessToken()

  const buildEndpoint = useCallback(
    (syncSnapshot: boolean) => {
      if (!candidateId || !vacancyId) {
        return null
      }
      const qs = new URLSearchParams({
        candidate: candidateId,
        vacancy: vacancyId,
        syncSnapshot: syncSnapshot ? "true" : "false",
      })
      if (companyId) {
        qs.set("company", companyId)
      }
      return `${SUPABASE_URL}${EDGE_PATH}?${qs.toString()}`
    },
    [candidateId, vacancyId, companyId]
  )

  const buildHeaders = useCallback(async () => {
    const headers: Record<string, string> = { "Content-Type": "application/json" }
    if (SUPABASE_ANON) {
      headers.apikey = SUPABASE_ANON
      headers.Authorization = `Bearer ${SUPABASE_ANON}`
    }
    headers["x-auth-token"] = `Bearer ${await getAccessToken()}`
    return headers
  }, [getAccessToken])

  const fetchNow = useCallback(async () => {
    const endpoint = buildEndpoint(true)
    if (!endpoint) {
      return
    }

    setLoading(true)
    setError(null)

    let mounted = true
    try {
      const headers = await buildHeaders()
      const response = await fetch(endpoint, {
        method: "GET",
        headers,
        credentials: "omit",
        cache: "no-store",
      })
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      const payload = (await response.json()) as CandidateApplicationResponse
      if (mounted) {
        setData(payload)
      }
    } catch (err) {
      if (mounted) {
        setError(err)
      }
    } finally {
      if (mounted) {
        setLoading(false)
      }
    }

    return () => {
      mounted = false
    }
  }, [buildEndpoint, buildHeaders])

  useEffect(() => {
    if (!candidateId || !vacancyId) {
      return
    }
    let cleanup: (() => void) | undefined
    ;(async () => {
      cleanup = await fetchNow()
    })()
    return () => {
      if (cleanup) {
        cleanup()
      }
    }
  }, [candidateId, vacancyId, fetchNow])

  useEffect(() => {
    if (!candidateId || !vacancyId) {
      return
    }
    const debouncedRefetch = debounce(() => {
      const endpoint = buildEndpoint(false)
      if (!endpoint) {
        return
      }
      ;(async () => {
        try {
          const headers = await buildHeaders()
          const res = await fetch(endpoint, {
            method: "GET",
            headers,
            credentials: "omit",
            cache: "no-store",
          })
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}`)
          }
          const payload = (await res.json()) as CandidateApplicationResponse
          setData(payload)
        } catch (err) {
          console.warn("[candidate realtime refetch] failed:", err)
        }
      })()
    }, 250)

    const channel = supabase
      .channel(`candidate-${candidateId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "applications",
          filter: `candidate_id=eq.${candidateId}`,
        },
        () => debouncedRefetch()
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [supabase, candidateId, vacancyId, buildEndpoint, buildHeaders])

  const applicationIds = useMemo(() =>
    Array.from(
      new Set(
        (data?.applications ?? [])
          .map((application) => application.applicationId)
          .filter((value): value is string => typeof value === "string" && value.length > 0)
      )
    ),
    [data?.applications]
  )

  useEffect(() => {
    if (!applicationIds.length) {
      return
    }

    const filter =
      applicationIds.length === 1
        ? `application_id=eq.${applicationIds[0]}`
        : `application_id=in.(${applicationIds.join(",")})`

    const channel = supabase
      .channel(`candidate-events-${applicationIds.join("-")}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "events",
          filter,
        },
        (payload) => {
          setData((previous) => {
            if (!previous) {
              return previous
            }

            const existing = previous.events ?? []
            const next = new Map(existing.map((event) => [event.id, event]))

            if (payload.eventType === "DELETE") {
              const oldRecord = payload.old as Database["public"]["Tables"]["events"]["Row"] | null
              if (oldRecord) {
                next.delete(oldRecord.id)
              }
            } else {
              const newRecord = payload.new as Database["public"]["Tables"]["events"]["Row"]
              next.set(newRecord.id, newRecord as CandidateApplicationEvent)
            }

            const sorted = Array.from(next.values()).sort((a, b) => (a.ts < b.ts ? 1 : -1))

            return {
              ...previous,
              events: sorted,
            }
          })
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [supabase, applicationIds])

  const refetch = useCallback(() => {
    const endpoint = buildEndpoint(false)
    if (!endpoint) {
      return
    }
    ;(async () => {
      try {
        const headers = await buildHeaders()
        const res = await fetch(endpoint, {
          method: "GET",
          headers,
          credentials: "omit",
          cache: "no-store",
        })
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`)
        }
        const payload = (await res.json()) as CandidateApplicationResponse
        setData(payload)
      } catch (err) {
        setError(err)
      }
    })()
  }, [buildEndpoint, buildHeaders])

  return { data, loading, error, refetch }
}
